const TIME_ZONE = 'Asia/Dhaka'
const INTAKE_MONTHS = [1, 4, 7, 10] as const
const DEFAULT_SEATS = 20
const ANCHOR_YEAR = 2026
const ANCHOR_QUARTER_INDEX = 2
const ANCHOR_BATCH_NUMBER = 3
const START_DAY_OVERRIDES: Record<string, number> = {
  '2027-01': 5,
}

const MONTH_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

function formatCadenceLabel(months: readonly number[]): string {
  const names = months.map((month) => MONTH_EN[month - 1])
  if (names.length <= 1) {
    return names[0] ?? ''
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

export const INTAKE_CADENCE_LABEL = formatCadenceLabel(INTAKE_MONTHS)
export const INTAKE_CADENCE_OR_LABEL = INTAKE_CADENCE_LABEL.replace(', and ', ', or ')

const MONTH_BN = [
  'জানুয়ারি',
  'ফেব্রুয়ারি',
  'মার্চ',
  'এপ্রিল',
  'মে',
  'জুন',
  'জুলাই',
  'আগস্ট',
  'সেপ্টেম্বর',
  'অক্টোবর',
  'নভেম্বর',
  'ডিসেম্বর',
] as const

type IntakeMonth = (typeof INTAKE_MONTHS)[number]

export type CalendarDate = {
  year: number
  month: number
  day: number
}

export type Intake = {
  batchNumber: number
  seats: number
  start: CalendarDate
  startIso: string
  month: string
  monthBangla: string
  year: string
  startLabel: string
}

export type Enrollment = {
  batchName: string
  nextBatchDate: string
  seats: string
  batchMonth: string
  batchMonthBangla: string
  batchYear: string
  batchLabel: string
  startIso: string
}

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

function toOrdinal(date: CalendarDate): number {
  return date.year * 10000 + date.month * 100 + date.day
}

function partNumber(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  const value = parts.find((part) => part.type === type)?.value
  if (!value) {
    throw new Error(`Unable to read ${type} in ${TIME_ZONE}`)
  }
  return Number(value)
}

export function getZonedDate(now: Date, timeZone = TIME_ZONE): CalendarDate {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(now)

  return {
    year: partNumber(parts, 'year'),
    month: partNumber(parts, 'month'),
    day: partNumber(parts, 'day'),
  }
}

function toIsoDate(date: CalendarDate): string {
  return `${date.year}-${pad2(date.month)}-${pad2(date.day)}`
}

function formatStartLabel(date: CalendarDate): string {
  return `${MONTH_EN[date.month - 1]} ${date.day}, ${date.year}`
}

function getStartDay(year: number, month: IntakeMonth): number {
  return START_DAY_OVERRIDES[`${year}-${pad2(month)}`] ?? 1
}

function getBatchNumber(year: number, month: IntakeMonth): number {
  const quarterIndex = INTAKE_MONTHS.indexOf(month)
  return ANCHOR_BATCH_NUMBER + (year - ANCHOR_YEAR) * 4 + (quarterIndex - ANCHOR_QUARTER_INDEX)
}

function buildIntake(start: CalendarDate, month: IntakeMonth): Intake {
  const monthName = MONTH_EN[start.month - 1]
  return {
    batchNumber: getBatchNumber(start.year, month),
    seats: DEFAULT_SEATS,
    start,
    startIso: toIsoDate(start),
    month: monthName,
    monthBangla: MONTH_BN[start.month - 1],
    year: String(start.year),
    startLabel: formatStartLabel(start),
  }
}

export function getNextIntake(now = new Date()): Intake {
  const today = getZonedDate(now)
  let year = today.year

  for (let step = 0; step < 12; step += 1) {
    const month = INTAKE_MONTHS[step % INTAKE_MONTHS.length]
    if (step > 0 && month === INTAKE_MONTHS[0]) {
      year += 1
    }

    const start: CalendarDate = {
      year,
      month,
      day: getStartDay(year, month),
    }

    if (toOrdinal(start) >= toOrdinal(today)) {
      return buildIntake(start, month)
    }
  }

  throw new Error('Unable to resolve next Skillo intake')
}

export function getEnrollment(now = new Date()): Enrollment {
  const intake = getNextIntake(now)
  return {
    batchName: `Batch ${intake.batchNumber}`,
    nextBatchDate: intake.startLabel,
    seats: String(intake.seats),
    batchMonth: intake.month,
    batchMonthBangla: intake.monthBangla,
    batchYear: intake.year,
    batchLabel: `${intake.month} Batch`,
    startIso: intake.startIso,
  }
}

export function isLegacyCampaignPath(pathname: string): boolean {
  return /^\/career-launch-batch-\d{4}$/.test(pathname)
}
